import React, { useCallback, useEffect, useState } from "react";
import HeaderSales from "../common/HeaderSales";
import Footer from "../common/Footer";
import { Link } from "react-router-dom";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import moment from "moment";
import axios from "axios";
import * as Constants from "../Constants/index";
import swal from "sweetalert";
import { saveAs } from "file-saver";
import Oval from "../../loader/CircleLoade";
import ResponsivePagination from "react-responsive-pagination";

const FALLBACK_TEXT = "N/A";
const DEFAULT_FILTERS = {
  mobile: "",
  fromDate: "",
  toDate: "",
  status: "",
};

const getDisplayValue = (value) => {
  if (value === null || value === undefined || value === "") {
    return FALLBACK_TEXT;
  }

  return value;
};

export default function NewSalesRedemptionReport() {
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [results, setResults] = useState([]);
  const [totalPages, setTotalPages] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [limit] = useState(10);
  const [errors, setErrors] = useState({});
  const [searchRecord, setSearchRecord] = useState();
  const [changeStatus, setStatus] = useState("");
  const [loading, setLoading] = useState(false);
  const [numberFilter, setNumberFilter] = useState("");
  const [appliedFilters, setAppliedFilters] = useState(DEFAULT_FILTERS);

  const formatDateForApi = (date, isEndOfDay = false) => {
    if (!date) {
      return "";
    }

    return moment(date).format(
      isEndOfDay ? "YYYY-MM-DD 23:59:59" : "YYYY-MM-DD 00:00:00"
    );
  };

  const buildListQuery = useCallback(
    (filters, pageNum) => {
      const params = new URLSearchParams({
        sortBy: "dateOfRequest",
        sortAsc: "true",
        limit: String(limit),
        page: String(pageNum),
      });

      if (filters.mobile) {
        params.append("mobile", filters.mobile);
      }
      if (filters.status) {
        params.append("status", filters.status);
      }

      const startDate = formatDateForApi(filters.fromDate);
      const endDate = formatDateForApi(filters.toDate, true);

      if (startDate) {
        params.append("start", startDate);
      }
      if (endDate) {
        params.append("end", endDate);
      }

      return `${Constants.baseUrl}reports/newRedemptionList/list?${params.toString()}`;
    },
    [limit]
  );

  const fetchRedemptionReport = useCallback(
    async (filters, pageNum) => {
      const token = localStorage.getItem("token");

      try {
        setLoading(true);
        const res = await axios.get(buildListQuery(filters, pageNum), {
          headers: {
            Authorization: `Bearer ${token}`,
            "Accept-Language": localStorage.getItem("langauge"),
            appVersion: Constants.av,
            platform: Constants.pt,
            company: Constants.company,
            program: Constants.program,
          },
        });

        if (res.status === 200 || res.status === 201) {
          if (res.data.code === 10001) {
            setResults(res.data.output.results || []);
            setTotalPages(res.data.output.totalPages || 0);
            setSearchRecord(undefined);
          } else if (res.data.code === 12002) {
            setResults([]);
            setTotalPages(0);
            setSearchRecord(null);
          } else {
            swal({
              text: res.data.msg,
              icon: "error",
              timer: 2000,
              buttons: false,
            });
          }
        } else {
          swal({
            text: res.data.msg,
            icon: "error",
            timer: 2000,
            buttons: false,
          });
        }
      } catch (error) {
        console.log(error);
      } finally {
        setLoading(false);
      }
    },
    [buildListQuery]
  );

  useEffect(() => {
    fetchRedemptionReport(appliedFilters, currentPage);
  }, [appliedFilters, currentPage, fetchRedemptionReport]);

  const handleSearch = () => {
    setErrors({});

    const nextFilters = {
      mobile: numberFilter,
      fromDate,
      toDate,
      status: changeStatus,
    };

    setAppliedFilters(nextFilters);
    setCurrentPage(1);

    window.scrollTo({
      top: 370,
      behavior: "smooth",
    });
  };

  const handleReset = () => {
    setErrors({});
    setNumberFilter("");
    setFromDate("");
    setToDate("");
    setStatus("");
    setAppliedFilters(DEFAULT_FILTERS);
    setCurrentPage(1);
  };

  const download = async () => {
    const token = localStorage.getItem("token");
    if (!token) {
      swal({
        text: "Authentication token is missing",
        timer: 2000,
        buttons: false,
      });
      return;
    }

    try {
      setLoading(true);

      const params = new URLSearchParams();
      if (appliedFilters.mobile) {
        params.append("mobile", appliedFilters.mobile);
      }
      if (appliedFilters.status) {
        params.append("status", appliedFilters.status);
      }
      if (appliedFilters.fromDate) {
        params.append("start", formatDateForApi(appliedFilters.fromDate));
      }
      if (appliedFilters.toDate) {
        params.append("end", formatDateForApi(appliedFilters.toDate, true));
      }

      const url = new URL(
        "reports/download/newredeemptionreport",
        Constants.baseUrl
      );
      url.search = params.toString();

      const response = await axios.get(url.toString(), {
        headers: {
          Authorization: `Bearer ${token}`,
          "Accept-Language": localStorage.getItem("langauge"),
          appVersion: Constants.av,
          platform: Constants.pt,
          company: Constants.company,
          program: Constants.program,
        },
        responseType: "blob",
      });

      if (response.status === 200) {
        const blob = new Blob([response.data], { type: "text/csv" });
        saveAs(
          blob,
          `NewRedeemptionReport_${new Date().toISOString().slice(0, 10)}.csv`
        );
      } else {
        throw new Error(`Unexpected status code: ${response.status}`);
      }
    } catch (error) {
      console.error("Download error:", error);

      let errorMessage = "Failed to download report";
      if (error.response) {
        if (error.response.data instanceof Blob) {
          try {
            const text = await error.response.data.text();
            const result = JSON.parse(text);
            errorMessage = result.message || errorMessage;
          } catch (parseError) {
            console.warn("Could not parse error blob", parseError);
          }
        } else {
          errorMessage = error.response.data?.message || errorMessage;
        }
      } else {
        errorMessage = error.message || errorMessage;
      }

      swal({
        text: errorMessage,
        timer: 2000,
        buttons: false,
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {loading ? <Oval /> : null}
      <HeaderSales />
      <section className="blog-area pt182">
        <div className="container">
          <div className="row">
            <div className="col-md-12 tab mb-20">
              <ul className="nav nav-tabs" role="tablist">
                <li role="presentation" className="active">
                  <Link to="#Section1" role="tab" data-toggle="tab">
                    New Redeemption Report
                  </Link>
                </li>
              </ul>
            </div>
          </div>
          <div className="row">
            <div className="col-md-12 col-sm-12 col-xs-12">
              <div className="all-single-blog rpt">
                <div className="row clearfix mb-15">
                  <div className="col-md-3 col-sm-12 col-xs-12">
                    <div className="form-group has-float-label style-two">
                      <label>Mobile</label>
                      <input
                        className="form-control reset"
                        type="text"
                        placeholder="Phone Number"
                        value={numberFilter}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (/^\d{0,10}$/.test(value)) {
                            setNumberFilter(value);
                          }
                        }}
                        maxLength="10"
                      />
                    </div>
                  </div>
                  <div className="col-md-3 col-sm-12 col-xs-12">
                    <div className="form-group has-float-label style-two">
                      <label>
                        From Date<sup></sup>{" "}
                      </label>
                      <DatePicker
                        selected={fromDate}
                        onChange={(date) => setFromDate(date)}
                        dateFormat="dd-MM-yyyy"
                        className="form-control"
                        id="Fromdate"
                        showYearDropdown
                        showMonthDropdown
                        autoComplete="off"
                        scrollableYearDropdown
                        maxDate={new Date()}
                      />
                    </div>
                    {errors.fromDate && (
                      <div className="error-message">{errors.fromDate}</div>
                    )}
                  </div>
                  <div className="col-md-3 col-sm-12 col-xs-12">
                    <div className="form-group has-float-label style-two">
                      <label>
                        To Date<sup></sup>{" "}
                      </label>
                      <DatePicker
                        selected={toDate}
                        onChange={(date) => setToDate(date)}
                        dateFormat="dd-MM-yyyy"
                        className="form-control"
                        id="todate"
                        showYearDropdown
                        showMonthDropdown
                        autoComplete="off"
                        scrollableYearDropdown
                        maxDate={new Date()}
                      />
                    </div>
                    {errors.toDate && (
                      <div className="error-message">{errors.toDate}</div>
                    )}
                  </div>
                  <div className="col-md-3 col-sm-12 col-xs-12">
                    <div className="form-group has-float-label style-two">
                      <label>
                        Select Status <sup></sup>{" "}
                      </label>
                      <select
                        className="form-control2"
                        style={{ color: "Gray" }}
                        value={changeStatus}
                        onChange={(event) => setStatus(event.target.value)}
                      >
                        <option value="">All </option>
                        <option value="CANCELLED">CANCELLED</option>
                        <option value="INPROCESS">In-Process </option>
                        <option value="DELIVERED">Delivered </option>
                        <option value="ORDERED">Ordered </option>
                      </select>
                    </div>
                    {errors.status && (
                      <div className="error-message">{errors.status}</div>
                    )}
                  </div>
                  <div className="col-md-12 col-sm-12 col-xs-12 text-center">
                    <div className="form-group has-float-label style-two">
                      <label className="invisible" style={{ width: "100%" }}>
                        hidden
                      </label>
                      <input
                        type="button"
                        className="btn login_btn"
                        value="Search"
                        id="btnSearch"
                        onClick={handleSearch}
                      />
                      <input
                        type="button"
                        className="btn login_btn"
                        value="Reset"
                        id="btnReset"
                        onClick={handleReset}
                      />
                    </div>
                  </div>
                </div>
                <div className="row">
                  <div className="col-md-12">
                    <div className="carrd1">
                      <div className="card-header card-header-warning">
                        <h4 className="card-title">
                          New Redeemption Report
                          {searchRecord === null ? null : (
                            <Link to="#" className="pull-right" onClick={download}>
                              <i className="fa fa-file-excel-o"></i> Export
                            </Link>
                          )}
                        </h4>
                      </div>
                      <div className="card-body table-responsive">
                        <table className="table table-striped mb_01" id="table-to-xls">
                          <thead>
                            <tr>
                              <th>S.No</th>
                              <th>Redemption ID</th>
                              <th>Member Name</th>
                              <th>Mobile Number</th>
                              <th>User Id</th>
                              <th>State</th>
                              <th>City</th>
                              <th>Date Of Request</th>
                              <th>Joining Date</th>
                              <th>Sales/Marketing Team Name</th>
                              <th>Total Earned Stars</th>
                              <th>Redeemed Stars</th>
                              <th>Redemption Request Date</th>
                              <th>Redemption Processed Date</th>
                              <th>Redemption Details</th>
                              <th>EGV Description</th>
                              <th>EGV Code</th>
                              <th>UPI Id</th>
                              <th>Account Number</th>
                              <th>Account Holder Name</th>
                              <th>IFSC Code</th>
                              <th>Bank Name</th>
                              <th>Status</th>
                              <th>Vendor Error Remarks</th>
                              <th>Product Code</th>
                              <th>Product Name</th>
                            </tr>
                          </thead>
                          {searchRecord === null ? (
                            <tbody>
                              <tr>
                                <td colSpan="26" style={{ textAlign: "center", fontSize: "16px" }}>
                                  No record found!
                                </td>
                              </tr>
                            </tbody>
                          ) : (
                            <tbody>
                              {results.map((item, index) => {
                                const serialNumber = (currentPage - 1) * limit + index + 1;

                                return (
                                  <tr key={`${item[0]}-${item[23] || index}`}>
                                    <td>{serialNumber}</td>
                                    <td>{getDisplayValue(item[0])}</td>
                                    <td>{getDisplayValue(item[1])}</td>
                                    <td>{getDisplayValue(item[2])}</td>
                                    <td>{getDisplayValue(item[3])}</td>
                                    <td>{getDisplayValue(item[4])}</td>
                                    <td>{getDisplayValue(item[5])}</td>
                                    <td>{getDisplayValue(item[6])}</td>
                                    <td>{getDisplayValue(item[7])}</td>
                                    <td>{getDisplayValue(item[8])}</td>
                                    <td>{getDisplayValue(item[9])}</td>
                                    <td>{getDisplayValue(item[10])}</td>
                                    <td>{getDisplayValue(item[11])}</td>
                                    <td>{getDisplayValue(item[12])}</td>
                                    <td>{getDisplayValue(item[13])}</td>
                                    <td>{getDisplayValue(item[14])}</td>
                                    <td>{getDisplayValue(item[15])}</td>
                                    <td>{getDisplayValue(item[16])}</td>
                                    <td>{getDisplayValue(item[17])}</td>
                                    <td>{getDisplayValue(item[18])}</td>
                                    <td>{getDisplayValue(item[19])}</td>
                                    <td>{getDisplayValue(item[20])}</td>
                                    <td>{getDisplayValue(item[21])}</td>
                                    <td>{getDisplayValue(item[22])}</td>
                                    <td>{getDisplayValue(item[23])}</td>
                                    <td>{getDisplayValue(item[24])}</td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          )}
                        </table>

                        {totalPages > 1 && (
                          <ResponsivePagination
                            current={currentPage}
                            total={totalPages}
                            onPageChange={(page) => setCurrentPage(page)}
                          />
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      <Footer />
    </>
  );
}

